/**
 * A custom hook that observes sections within a scrollable container
 * and updates the active step based on which section is most visible.
 *
 * @param {React.RefObject<HTMLDivElement | null>} containerRef
 *   Ref to the scrollable container element that holds the sections.
 *
 * @param {React.MutableRefObject<(HTMLDivElement | null)[]>} sectionRefs
 *   An array of refs to the section elements being tracked.
 *
 * @param {React.Dispatch<React.SetStateAction<number>>} setActiveStep
 *   State setter function used to update the currently active step index.
 *
 * @returns {() => void | undefined}
 *   A cleanup function to disconnect the observer when the component unmounts,
 *   or `undefined` if the container ref is not available.
 *
 * @example
 * const containerRef = useRef<HTMLDivElement>(null);
 * const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
 * const [activeStep, setActiveStep] = useState(0);
 *
 * useEffect(() => {
 *   const cleanup = useStepObserver(containerRef, sectionRefs, setActiveStep);
 *   return cleanup;
 * }, []);
 */
export const useStepObserver = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  sectionRefs: React.MutableRefObject<(HTMLDivElement | null)[]>,
  setActiveStep: React.Dispatch<React.SetStateAction<number>>
) => {
  const container = containerRef.current;
  if (!container) return;

  const observer = new IntersectionObserver(
    (entries) => {
      let maxRatio = 0;
      let visibleStep = -1;

      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const index = sectionRefs.current.indexOf(entry.target as HTMLDivElement);
        if (index > -1 && entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          visibleStep = index;
        }
      });

      if (visibleStep !== -1) {
        setActiveStep((prev) => (prev !== visibleStep ? visibleStep : prev));
      }
    },
    {
      root: container,
      threshold: [0.9],
    }
  );

  sectionRefs.current.forEach((section) => section && observer.observe(section));
  return () => observer.disconnect();
};
